import React from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Paper from "@material-ui/core/Paper";
import Draggable from "react-draggable";
import { makeStyles } from "@material-ui/core/styles";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import TextField from "@material-ui/core/TextField";
import templateService from "../services/TemplateService";
import EmailTemplateService from "../services/EmailTemplateService";
import notify from "../utils/notifier";
import Toolbar from "@material-ui/core/Toolbar";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import DeleteIcon from "@material-ui/icons/Delete";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import Util from "../utils/Util";
import { useTranslation } from "react-i18next";
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import { hasPrivilege } from "../utils/privileges_utility";

const useStyles = makeStyles(theme => ({
  button: {
    margin: theme.spacing(1)
  },
  input: {
    display: "none"
  },
  floatLeft: {
    float: "left"
  },
  formContainer: {
    display: "flex",
    flexWrap: "wrap"
  },
  flexItem: {
    flex: 1
  },
  textField: {
    paddingRight: theme.spacing(4)
  },
  hiddenField: {
    visibility: "hidden",
    padding: "20px"
  },
  fileUploadWrapper: {
    background: "#f8f8f8",
    minHeight: "75px",
    display: "flex",
    alignItems: "center",
    borderRadius: "5px",
    padding: "20px"
  },
  fileUpload:{
    maxWidth:400,
    overflowWrap:"break-word"
  },
  disabledIcon: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  radioGroup: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

function PaperComponent(props) {
  return (
    <Draggable cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

function tryParseJSON (jsonString){
  try {
      var o = JSON.parse(jsonString);
      if (o && typeof o === "object") {
          return true;
      }
  }
  catch (e) { 
      return false;
  }
  return false;
};

export default function HtmlUploadModal(props) {
  const [t, i18n] = useTranslation();
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);

  const { useState } = React;
  const [templateName, setTemplateName] = useState("");
  const [orgId, setOrgId] = useState("");
  const [appId, setAppId] = useState("");
  const [metadata, setMetadata] = useState("");
  const [file, setFile] = useState("");
  const [backdrop, setBackdrop] = React.useState(false);
  
  // Check if user has required privileges
  const canUpload = hasPrivilege("DOC_TEMP_UPLOAD");
  const canAmend = hasPrivilege("DOC_TEMP_AMND");
  
  // Determine if the current operation should be allowed
  const isCreateMode = props.mode === "create";
  const isOperationAllowed = isCreateMode ? canUpload : canAmend;
  
  const setInitialState = () => {
    setTemplateName("");
    setOrgId("");
    setAppId("");
    setMetadata("");
    setFile("");
  }

  const setData = data => {
    setTemplateName({ value: data.templateName });
    setOrgId({ value: data.organizationId });
    setAppId({ value: data.applicationId });
    
    let isMetadataObj = typeof data.metadata === "object" ? true : false;
    if (isMetadataObj) {
      let metadataStr = Util.toString(data.metadata);
      setMetadata({ value: metadataStr });
    } else {
      setMetadata({ value: data.metadata });
    }
    // setFile(data.file);
  };

  const handleClickOpen = () => {
    // Check for permissions before opening dialog
    if (!isOperationAllowed) {
      const permissionType = isCreateMode ? "upload new templates" : "edit templates";
      notify({
        type: "error",
        message: `You don't have permission to ${permissionType}`
      });
      return;
    }
    
    if (props.mode === "edit") {
      setData(props.data);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setInitialState();
  };

  const handleClickDeleteFile = () => {
    setFile({ value: null });
  };

  const validateOnFileChange = _file => {
    if (!_file) return;

    if (_file) {
      let reg = /(.*?)\.(html)$/;
      let errorMessage = "Only .html file is supported";

      if (!_file.name.match(reg)) {
        notify({
          type: "error",
          message: errorMessage
        });
        return;
      }
    }
  };

  const buildRequestObj = () => {
    let invalidFields = [];
    let isEditMode = props.mode === "edit" ? true : false;

    if (isEditMode) {
      invalidFields = validateFields(templateName, orgId, appId,metadata);
    } else {
      invalidFields = validateFields(templateName, orgId, appId, metadata,file);
    }
    if (invalidFields.length) {
      notify({
        type: "error",
        message:
          "Invalid Fields(" +
          invalidFields.length +
          "): Please provide valid input."
      });
      return;
    }
     if (!tryParseJSON(metadata.value) || !Object.keys(JSON.parse(metadata.value)).length) {
      notify({
        type: "error",
        message: "Error: Invalid metadata json input"
      });
      return;
    } 
    if (file.value) {
      let reg = /(.*?)\.(html)$/;
      let errorMessage = "Only .html file is supported";

      if (!file.value.name.match(reg)) {
        notify({
          type: "error",
          message: errorMessage
        });
        return;
      }
    }

    var formData = new FormData();
    formData.append("templateName", templateName.value);
    formData.append("organizationId", orgId.value);
    formData.append("applicationId", appId.value);
    formData.append("file", file.value);
    // let metadataJson = Util.toJSONObj(metadata.value);
    formData.append("metadata", metadata.value);
    
    return formData;
  };

  const handleSubmit = () => {
    // Double-check permission before submitting
    if (!canUpload) {
      notify({
        type: "error",
        message: "You don't have permission to upload templates"
      });
      handleClose();
      return;
    }
    
    setOpen(true);
    let formData = buildRequestObj();
    if (!formData) return;
    let service;
    if (!(props.service === undefined) && props.service === "email") {
      service = EmailTemplateService;
    } else {
      service = templateService;
    }
    setBackdrop(true);
    service.uploadTemplate(formData).then(
      response => {
        setBackdrop(false);
            notify({
              type: "success",
              message: "Successfully uploaded template file.."
            });
            props.onUploadComplete({ data: response.data, mode: "create" });
        handleClose();
        
      },
      error => {
        setBackdrop(false);
        notify({
          type: "error",
          message: "Error: " + error.response.data.data
        });
      }
    );
    setInitialState();
  };

  const handleUpdate = () => {
    // Double-check permission before updating
    if (!canAmend) {
      notify({
        type: "error",
        message: "You don't have permission to modify templates"
      });
      handleClose();
      return;
    }
    
    let formData = buildRequestObj();
    if (!formData) return;
    setBackdrop(true);
    if (!(props.service === undefined) && props.service === "email") {
      EmailTemplateService.updateTemplate(props.data.templateName, props.data.organizationId, formData).then(
        response => {
          setBackdrop(false);
          var formDataObj = {};
          formData.forEach((value, key) => {
            formDataObj[key] = value;
          });
          let metadataJson = Util.toJSONObj(metadata.value);
          formData["metadata"] = metadataJson;
          props.onUploadComplete({
            data: formDataObj,
            mode: "edit",
            recordId: props.data.templateName,
            orgId: props.data.organizationId
          });
          notify({
            type: "success",
            message: "Successfully updated email template file.."
          });
          handleClose();
        },
        error => {
          setBackdrop(false);
          notify({
            type: "error",
            message: "Error: " + error.response.data.data
          });
        }
      );
    } else {
      templateService.updateTemplate(props.data.templateId, formData).then(
        response => {
          setBackdrop(false);
          var formDataObj = {};
          formData.forEach((value, key) => {
            formDataObj[key] = value;
          });
          formDataObj["updatedAt"] = props.data.updatedAt;
          let metadataJson = Util.toJSONObj(metadata.value);
          formData["metadata"] = metadataJson;
          
          props.onUploadComplete({
            data: formDataObj,
            mode: "edit",
            recordId: props.data.templateId
          });
          notify({
            type: "success",
            message: "Successfully updated template file.."
          });
          handleClose();
        },
        error => {
          setBackdrop(false);
          notify({
            type: "error",
            message: "Error: " + error.response.data.data
          });
        }
      );
    }
  };

  const validateFields = (...fields) => {
    let errorFields = [];
    fields.forEach(field => {
      if (!field.value || typeof field.value === "undefined") {
        errorFields.push(field);
      }
    });
    return errorFields;
  };
  const gettemplateIdentifier = (props) => {
    let templateString = props.service === undefined ? props.data.templateId : props.data.templateName;
    return templateString;
  }


  return (
    <div>
      {props.mode === "create" ? (
        // Upload button (only shown if user has upload permission)
        canUpload ? (
          <Button
            color="primary"
            className={classes.button}
            onClick={handleClickOpen}
            startIcon={<CloudUploadIcon />}
          >
            {t("Upload")}
          </Button>
        ) : (
          <Tooltip title="Upload (Requires permission)" placement="top">
            <span className={classes.disabledIcon}>
              <Button
                color="primary"
                className={classes.button}
                disabled={true}
                startIcon={<CloudUploadIcon />}
              >
                {t("Upload")}
              </Button>
            </span>
          </Tooltip>
        )
      ) : (
        // Edit button (only shown if user has amend permission)
        canAmend ? (
          <Tooltip title="Edit" placement="top">
            <IconButton
              aria-label="edit"
              color="primary"
              size="small"
              onClick={handleClickOpen}
            >
              <EditIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Edit (Requires permission)" placement="top">
            <span className={classes.disabledIcon}>
              <IconButton
                aria-label="edit"
                color="primary"
                size="small"
                disabled={true}
              >
                <EditIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
        )
      )}

<Dialog open={open} PaperComponent={PaperComponent}>
        <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
          {props.mode === "edit"
            ? t("Modify Template") + " " + gettemplateIdentifier(props)
            : t("Upload a new Template")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Please enter all the required fields.")}
          </DialogContentText>
          <div className={classes.formContainer}>
            { props.mode !== "edit" && <div className={classes.flexItem}>
              <TextField
                id="standard-basic"
                className={classes.textField}
                label={t("Template Name")}
                margin="normal"
                type={'text'}
                required
                fullWidth
                value={templateName.value}
                onChange={e => {
                  const val = e.target.value;
                  setTemplateName(prevState => {
                    return { ...prevState, value: val };
                  });
                }}
              />
            </div>}
            {props.mode !== "edit" && <div className={classes.flexItem}>
              <TextField
                id="standard-basic"
                required
                type={'text'}
                className={classes.textField}
                label={t("Organization ID")}
                margin="normal"
                fullWidth
                value={orgId.value}
                onChange={e => {
                  const val = e.target.value;
                  setOrgId(prevState => {
                    return { ...prevState, value: val };
                  });
                }}
              />
            </div>}
          </div>
          <div>
            <TextField
              id="standard-multiline-static"
              label={t("Application ID")}
              required
              type={'text'}
              className={classes.textField}
              margin="normal"
              fullWidth
              value={appId.value}
              onChange={e => {
                const val = e.target.value;
                setAppId(prevState => {
                  return { ...prevState, value: val };
                });
              }}
            />
          </div>
          <div>
            <TextField
              id="standard-multiline-static"
              label={t("Metadata")}
              multiline
              required
              rows="2"
              className={classes.textField}
              margin="normal"
              fullWidth
              value={metadata.value}
              onChange={e => {
                const val = e.target.value;
                setMetadata(prevState => {
                  return { ...prevState, value: val };
                });
              }}
            />
          </div>

          <div>
            <label htmlFor="file" className={classes.fileUploadWrapper}>
              <Toolbar>
                <Typography variant="h6" className={classes.fileUpload}>
                  {file.value
                    ? file.value.name
                    : t("Select HTML Template to upload (.html only)")}
                </Typography>
                {file.value ? (
                  <IconButton
                    aria-label="delete"
                    color="secondary"
                    size="small"
                    onClick={handleClickDeleteFile}
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                ) : (
                    ""
                  )}
              </Toolbar>
            </label>
            <input
              id="file"
              name="file"
              className={classes.hiddenField}
              type="file"
              accept=".html"
              required
              onChange={e => {
                const val = e.target.files[0];
                setFile(prevState => {
                  return { ...prevState, value: val };
                });
                validateOnFileChange(val);
              }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose} color="secondary">
            {t("CANCEL")}
          </Button>
          {props.mode === "edit" ? (
            <Button onClick={handleUpdate} color="primary">
              {t("UPDATE")}
            </Button>
          ) : (
              <Button onClick={handleSubmit} color="primary">
                {t("SUBMIT")}
              </Button>
            )}
            
        </DialogActions>
      </Dialog>
      <Backdrop open={backdrop} style={{ color: "primary", zIndex: 1000000 }} >
                 <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
} 